import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { isPendingOAuthUserId, shouldFetchUserScoped } from '@/util/authTokens';

/**
 * Centralized builder for current user queries with auth-aware gating.
 *
 * Behavior-preserving: matches existing call sites that gate by
 * shouldFetchUserScoped(userId) or by !isPendingOAuthUserId for some screens.
 */
export function getCurrentUserScopedQueryOptions(params?: { userId?: string | null }) {
  const userId = params?.userId;

  return {
    ...getCurrentUserV2QueryOptions(userId || undefined),
    enabled: shouldFetchUserScoped(userId || undefined),
  };
}

/**
 * Variant that only enables when authenticated (not pending OAuth),
 * used by Inquiry Demo UIs that require a DB-backed user.
 */
export function getCurrentUserAuthenticatedOnlyQueryOptions() {
  return {
    ...getCurrentUserV2QueryOptions(),
    // caller will pass userId via react-query key from the underlying options
    // we only add the enable guard here consistent with existing code
    enabled: false,
  } as ReturnType<typeof getCurrentUserV2QueryOptions> & { enabled: boolean };
}

/**
 * Authenticated-only variant with explicit userId gating.
 * Matches pattern: enabled: !!userId && !isPendingOAuthUserId(userId)
 */
export function getCurrentUserAuthenticatedQueryOptions(userId?: string | null) {
  return {
    ...getCurrentUserV2QueryOptions(userId || undefined),
    enabled: !!userId && !isPendingOAuthUserId(userId || undefined),
  } as ReturnType<typeof getCurrentUserV2QueryOptions> & { enabled: boolean };
}
